import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Track Your Missionary Support-Raising Donors: Spreadsheet vs. Dedicated Platform';
const DESCRIPTION =
    "A spreadsheet works fine for donor tracking — until it doesn't. What to record for every donor no matter which tool you use, why a spreadsheet is a legitimate place to start, and the signs it's time to move to something built for it.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover5.jpg',
            datePublished: '2026-08-08',
            dateModified: '2026-08-08',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-track-missionary-support-raising-donors',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What should I track for each missionary support-raising donor?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "At minimum: contact info, how they know you, gift type (one-time or recurring) and amount, the date of their last gift, how they prefer to hear from you, and any personal notes — a health issue you're praying for, a name of a family member — that make your next message feel personal rather than mass-produced.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is a spreadsheet good enough for tracking missionary donors?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "For a first support base of a few dozen donors, yes — a spreadsheet is honest, free, and forces you to actually look at your data. It starts to break down once you need to know who's overdue for an update, who missed a payment, or you're managing this alongside newsletters and giving pages in three different tools.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'When should a missionary move from a spreadsheet to a donor CRM?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "The clearest signal is when you can no longer answer 'who haven't I thanked' or 'whose card just expired' without manually scanning rows. That's usually somewhere between 40 and 80 donors, though it depends more on how many tools you're juggling than the raw donor count.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Can I track donors and collect giving in the same place?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "That's the main advantage of a platform built specifically for missionary support — the giving page, the payment history, and the donor CRM live in one system, so a new gift automatically updates the donor's record instead of requiring you to copy it over by hand.",
                    },
                },
            ],
        },
    ],
};

export default function HowToTrackMissionarySupportRaisingDonors() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-track-missionary-support-raising-donors' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-track-donors-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover5.jpg" alt="A notebook and laptop, representing the record-keeping behind consistent missionary donor follow-up" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most missionaries start support raising with no donor-tracking system at all — gifts arrive, get a mental "thank you," and the details live in memory. That works for the first ten donors. It quietly stops working long before anyone notices, because the failure mode isn't a crisis, it's a slow drift: a donor who never got a personal thank-you, an update that skipped someone by accident, a lapsed gift nobody flagged. Tracking donors deliberately — in a spreadsheet or a dedicated tool — is what keeps that drift from happening.</p>

                    <h2>What to track for every donor, no matter which tool you use</h2>

                    <p>Before choosing a spreadsheet or a platform, get clear on what actually needs recording. The list is shorter than it feels:</p>
                    <ul>
                        <li><strong>Contact info</strong> — name, email, and how they prefer to hear from you (email, text, mail).</li>
                        <li><strong>How you know them</strong> — church, family, college, a specific introduction. This is what makes a follow-up message feel personal instead of generic.</li>
                        <li><strong>Gift type and amount</strong> — one-time or recurring, and how much, so you know your actual monthly support total at a glance.</li>
                        <li><strong>Date of last gift</strong> — the single most useful field for catching a lapsed recurring donor before too much time passes.</li>
                        <li><strong>Personal notes</strong> — a health concern you're praying for, a kid's name, a detail from your last conversation. These are what turn a thank-you from a form letter into something a donor actually remembers.</li>
                    </ul>

                    <h2>The case for starting with a spreadsheet</h2>

                    <p>A spreadsheet is a legitimate, honest place to start. It's free, it's flexible, and — unlike a system that hides the data behind dashboards — it forces you to actually look at every row when you update it. For a first support base of a few dozen donors, a well-kept spreadsheet with the fields above will genuinely serve you well, and there's no reason to add a new tool before you need one.</p>

                    <p>The habit that makes a spreadsheet work is a fixed review rhythm — the same discipline covered in <a href="/resources/how-often-to-update-supporters-newsletter-cadence">how often to update your supporters</a>: pick a day each month to scan the "last gift" column for anyone overdue, and treat that scan as non-negotiable, not something you'll get to eventually.</p>

                    <h2>Where spreadsheets break down</h2>

                    <p>The cracks show up gradually, not all at once. A spreadsheet can't tell you a card is about to expire — you find out when the payment fails and a donor quietly stops giving without saying anything. It can't merge with your newsletter tool, so sending updates means exporting a list, hoping it's current, and pasting it somewhere else. And it has no memory of what you've already sent someone, so as your list grows past a few dozen names, "did I already thank this person" becomes a real, recurring question instead of an obvious one.</p>

                    <p>None of this means the spreadsheet approach was wrong — it means you've outgrown a general-purpose tool being asked to do a specific job. That's a good problem; it means your support base grew.</p>

                    <h2>What a dedicated donor CRM adds</h2>

                    <p>A platform built specifically for missionary support ties the donor record to the actual giving activity, so a new gift or a failed payment updates the donor automatically instead of requiring you to notice and re-enter it. That closes the two biggest spreadsheet gaps at once: you get an automatic flag when a card is expiring or a payment fails — see <a href="/resources/what-to-do-when-a-donors-card-expires">what to do when a donor's card expires</a> — and your donor list stays in the same system as your newsletter, so sending an update doesn't require an export step at all.</p>

                    <h2>A simple system that works at any size</h2>

                    <p>Whichever tool you use, the system matters more than the software. Review your full donor list on a fixed monthly schedule, not "when you get to it." Flag anyone overdue for a thank-you or a personal note. Watch for lapsed or failed recurring gifts and follow up within days, not months — see <a href="/resources/donor-retention-for-missionaries">donor retention for missionaries</a> for why that speed matters. And keep the personal notes current, because they're what make years three, four, and five of a support relationship feel as warm as year one.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What should I track for each missionary support-raising donor?</strong><br />
                    Contact info, how they know you, gift type and amount, date of last gift, communication preference, and personal notes that make follow-up feel personal.</p>

                    <p><strong>Is a spreadsheet good enough for tracking missionary donors?</strong><br />
                    Yes, for a first support base of a few dozen donors. It breaks down once you need to catch overdue updates or failed payments automatically.</p>

                    <p><strong>When should a missionary move from a spreadsheet to a donor CRM?</strong><br />
                    Usually somewhere between 40 and 80 donors, or as soon as you can no longer answer "who haven't I thanked" without manually scanning every row.</p>

                    <p><strong>Can I track donors and collect giving in the same place?</strong><br />
                    Yes — a platform built for missionary support keeps the giving page, payment history, and donor CRM in one system, so a new gift updates the donor record automatically.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/donor-retention-for-missionaries">Donor Retention for Missionaries</a></li>
                        <li><a href="/resources/what-to-do-when-a-donors-card-expires">What to Do When a Donor's Card Expires</a></li>
                        <li><a href="/resources/how-often-to-update-supporters-newsletter-cadence">How Often to Update Your Supporters</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built the donor CRM into the platform because we kept hearing the same story: a great support base, and no real system for keeping track of it once it grew past a few dozen names.</p>
                    <p><small><em>Last updated: 2026-08-08.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-track-missionary-support-raising-donors" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
